import { cn } from '@morpho-org/ui-v6/utils'
import { forwardRef, type HTMLAttributes } from 'react'

import { CardText, type CardTextProps } from './CardText'

export interface TableErrorProps extends Omit<HTMLAttributes<HTMLDivElement>, 'children'> {
  /** Error message to display */
  text?: string
  /** Height of the container */
  height?: string
  /** Props for the CardText component */
  cardTextProps?: Omit<CardTextProps, 'text' | 'height'>
}

/**
 * TableError component - Tailwind replica of dapp-ui-v5 TableError
 *
 * A component to display when an error occurs while loading a table.
 * Renders a centered message with the Morpho logo icon.
 *
 * @example
 * ```tsx
 * <TableError />
 * <TableError text="Failed to load data" height="300px" />
 * ```
 */
const TableError = forwardRef<HTMLDivElement, TableErrorProps>(
  (
    {
      text = 'An error occurred while loading the table.',
      height = '250px',
      className,
      cardTextProps,
      ...props
    },
    ref
  ) => {
    return (
      <div ref={ref} className={cn('gap-sm flex w-full flex-col', className)} {...props}>
        <CardText text={text} height={height} {...cardTextProps} />
      </div>
    )
  }
)

TableError.displayName = 'TableError'

export { TableError }
